// src/components/Common/DirectMessagesView.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Button,
  Menu,
  MenuItem,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  Chip,
  Divider,
  CircularProgress,
  InputAdornment,
  Stack,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import ReportViewer from './ReportViewer';
import {
  Send as SendIcon,
  Search as SearchIcon,
  ChatBubbleOutline as ChatIcon,
  Person as PersonIcon,
  DoneAll as DoneAllIcon,
  Flag as FlagIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  getDirectConversations,
  getDirectMessageThread,
  sendDirectMessage,
  markDirectMessagesRead
} from '../../services/directMessages';
import { getQaQueue } from '../../services/reports';
import { formatPatientName } from '../../services/utils';

// Helper to get calendar date key for grouping messages (YYYY-MM-DD)
const getDateKey = (dateString) => {
  if (!dateString) return 'unknown';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return 'unknown';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// WhatsApp-style date divider label ("Today", "Yesterday", or "DD/MM/YYYY")
const formatMessageDateDivider = (dateString) => {
  if (!dateString) return 'Today';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Today';

  const now = new Date();
  const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((today - msgDay) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  }
  if (diffDays === 1) {
    return 'Yesterday';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Format timestamp for conversations sidebar list
const formatConversationTimestamp = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((today - msgDay) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) {
    return 'Yesterday';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export default function DirectMessagesView({ initialTargetUser = null, initialContext = null, onClose = null, onViewReport = null }) {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const { user: currentUser } = useAuth();
  const myId = (currentUser?._id || currentUser?.id)?.toString();

  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [selectedUser, setSelectedUser] = useState(() => {
    const initId = (initialTargetUser?._id || initialTargetUser?.id)?.toString();
    return (initialTargetUser && initId && initId !== myId) ? initialTargetUser : null;
  });
  const [activeContext, setActiveContext] = useState(initialContext);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [error, setError] = useState('');
  const [qaReports, setQaReports] = useState([]);
  const [flaggedMenuAnchor, setFlaggedMenuAnchor] = useState(null);
  const [reportViewerState, setReportViewerState] = useState({
    open: false,
    studyId: null,
    study: null,
    reportType: 'dycovue-report'
  });

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadQaReports = useCallback(async () => {
    try {
      const data = await getQaQueue();
      const list = Array.isArray(data?.reports) ? data.reports : [];
      setQaReports(list.filter((r) => r.facilityReview?.status === 'flagged'));
    } catch (err) {
      console.warn('Could not load QA reports in DirectMessagesView:', err);
    }
  }, []);

  useEffect(() => {
    loadQaReports();
  }, [loadQaReports]);

  // Compute flagged reports relevant to currently selected chat partner
  const userFlaggedReports = React.useMemo(() => {
    if (!selectedUser || !Array.isArray(qaReports)) return [];
    const targetId = (selectedUser._id || selectedUser.id)?.toString();
    const related = qaReports.filter((r) => {
      const flaggerId = (r.facilityReview?.flaggedBy?._id || r.facilityReview?.flaggedBy)?.toString();
      const authorId = (r.createdBy?._id || r.createdBy)?.toString();
      const radId = (r.study?.assignedRadiologist?._id || r.study?.assignedRadiologist)?.toString();
      return (flaggerId && flaggerId === targetId) || (authorId && authorId === targetId) || (radId && radId === targetId);
    });
    return related.length > 0 ? related : qaReports;
  }, [selectedUser, qaReports]);

  const handleOpenReportViewer = (context, relatedStudy, relatedReport) => {
    const studyId =
      context?.studyId ||
      (typeof relatedStudy === 'object' ? (relatedStudy?._id || relatedStudy?.pacsStudyId) : relatedStudy) ||
      (typeof relatedReport === 'object' ? (relatedReport?.study?._id || relatedReport?.study) : null) ||
      context?.studyUID;
    const studyObj =
      (typeof relatedStudy === 'object' && relatedStudy) ? relatedStudy :
      (typeof relatedReport?.study === 'object' && relatedReport?.study) ? relatedReport.study :
      (context?.study || null);

    const resolvedStudyId = studyId || (studyObj?._id || studyObj?.pacsStudyId || studyObj?.studyInstanceUID);

    if (resolvedStudyId) {
      setReportViewerState({
        open: true,
        studyId: resolvedStudyId,
        study: studyObj,
        reportType: 'dycovue-report'
      });
    }
    if (onViewReport && (studyObj || resolvedStudyId)) {
      try {
        onViewReport(studyObj || { _id: resolvedStudyId }, 'dycovue-report');
      } catch (e) {
        console.warn('onViewReport error:', e);
      }
    }
  };

  const handleSelectReportContext = (report) => {
    setActiveContext({
      type: 'flagged_report',
      reportId: report._id,
      studyId: report.study?._id || report.study,
      studyUID: report.study?.studyInstanceUID || report.studyInstanceUID,
      patientName: report.patientName || report.study?.patientName,
      modality: report.modality || report.study?.modality,
      concerns: report.facilityReview?.concerns
    });
    setFlaggedMenuAnchor(null);
  };

  useEffect(() => {
    if (initialTargetUser) {
      const targetId = (initialTargetUser._id || initialTargetUser.id)?.toString();
      if (targetId && targetId !== myId) {
        setSelectedUser(initialTargetUser);
      }
    }
  }, [initialTargetUser, myId]);

  // Guard against self-selection
  useEffect(() => {
    if (selectedUser) {
      const id = (selectedUser._id || selectedUser.id)?.toString();
      if (id && id === myId) {
        setSelectedUser(null);
      }
    }
  }, [selectedUser, myId]);

  useEffect(() => {
    if (initialContext) {
      setActiveContext(initialContext);
    }
  }, [initialContext]);

  // Fetch all conversations
  const loadConversations = useCallback(async () => {
    try {
      setError('');
      const data = await getDirectConversations();
      const list = Array.isArray(data) ? data : (data?.conversations || []);
      setConversations(list);

      // If initial target user wasn't in conversations, we can still show them as selected
      if (initialTargetUser) {
        const initId = (initialTargetUser._id || initialTargetUser.id)?.toString();
        if (initId && initId !== myId) {
          setSelectedUser((curr) => {
            const currId = (curr?._id || curr?.id)?.toString();
            return (curr && currId !== myId) ? curr : initialTargetUser;
          });
        }
      }
    } catch (err) {
      console.error('Failed to load conversations:', err);
      setError('Could not load conversations. Please check your connection.');
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  }, [initialTargetUser, myId]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Handle global open-direct-message events
  useEffect(() => {
    const handleOpenMessage = (e) => {
      const { user, context } = e.detail || {};
      const targetId = (user?._id || user?.id)?.toString();
      if (user && targetId && targetId !== myId) {
        setSelectedUser(user);
        if (context) setActiveContext(context);
      }
    };

    window.addEventListener('open-direct-message', handleOpenMessage);
    return () => window.removeEventListener('open-direct-message', handleOpenMessage);
  }, [myId]);

  // Fetch thread when selectedUser changes
  const loadThread = useCallback(async (targetId) => {
    if (!targetId) return;
    setLoadingMessages(true);
    try {
      const thread = await getDirectMessageThread(targetId);
      const msgList = Array.isArray(thread) ? thread : (thread?.messages || []);
      setMessages(msgList);
      // Mark messages read
      await markDirectMessagesRead(targetId);
      // Update unread count in conversations state
      setConversations((prev) =>
        (Array.isArray(prev) ? prev : []).map((c) => {
          const other = c.otherUser || c.partner;
          return other?._id === targetId ? { ...c, unreadCount: 0 } : c;
        })
      );
    } catch (err) {
      console.error('Failed to load thread:', err);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    const id = selectedUser?._id || selectedUser?.id;
    if (id) {
      loadThread(id);
    }
    // Track active chat user to suppress toast popups while actively viewing the conversation
    window.__activeChatUserId = id ? id.toString() : null;
    return () => {
      window.__activeChatUserId = null;
    };
  }, [selectedUser, loadThread]);

  // Scroll to bottom whenever messages update
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Real-time listener via custom event
  useEffect(() => {
    const handleIncomingMessage = (e) => {
      const msg = e.detail;
      if (!msg) return;

      const targetId = (selectedUser?._id || selectedUser?.id)?.toString();
      const senderId = (msg.sender?._id || msg.sender)?.toString();
      const recipientId = (msg.recipient?._id || msg.recipient)?.toString();

      // If belongs to current selected conversation, append with deduplication
      if (targetId && (senderId === targetId || recipientId === targetId)) {
        setMessages((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          // If our chat partner sent us a message, our prior sent messages are confirmed read
          const marked = (senderId === targetId)
            ? list.map((m) => {
                const s = (m.sender?._id || m.sender?.id || m.sender)?.toString();
                if (s === myId && !m.read) {
                  return { ...m, read: true, readAt: new Date() };
                }
                return m;
              })
            : list;

          const incomingId = String(msg._id || msg.id || '');
          if (incomingId && marked.some((m) => String(m._id || m.id || '') === incomingId)) {
            return marked;
          }
          return [...marked, msg];
        });

        if (senderId === targetId) {
          markDirectMessagesRead(targetId).catch((err) =>
            console.error('Error marking message read:', err)
          );
        }
      }

      // Refresh conversations list to update snippet
      loadConversations();
    };

    window.addEventListener('new-direct-message', handleIncomingMessage);
    return () => window.removeEventListener('new-direct-message', handleIncomingMessage);
  }, [selectedUser, loadConversations, myId]);

  // Real-time listener for read receipts (when partner reads our messages)
  useEffect(() => {
    const handleMessagesRead = (e) => {
      const data = e.detail;
      if (!data) return;

      const targetId = (selectedUser?._id || selectedUser?.id)?.toString();
      const readerId = data.readerId?.toString();

      // If the reader is our currently open chat partner, mark all our outgoing messages as read
      if (targetId && readerId && targetId === readerId) {
        setMessages((prev) => {
          if (!Array.isArray(prev)) return prev;
          let changed = false;
          const updated = prev.map((m) => {
            const sender = (m.sender?._id || m.sender?.id || m.sender)?.toString();
            if (sender === myId && !m.read) {
              changed = true;
              return { ...m, read: true, readAt: data.readAt || new Date() };
            }
            return m;
          });
          return changed ? updated : prev;
        });
      }
    };

    window.addEventListener('direct-messages-read', handleMessagesRead);
    return () => window.removeEventListener('direct-messages-read', handleMessagesRead);
  }, [selectedUser, myId]);

  // Mark messages read when window gains focus while in active conversation
  useEffect(() => {
    const handleFocus = () => {
      const targetId = (selectedUser?._id || selectedUser?.id)?.toString();
      if (targetId) {
        markDirectMessagesRead(targetId).catch((err) =>
          console.error('Error marking messages read on focus:', err)
        );
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [selectedUser]);

  // Send message
  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    const recipientId = selectedUser?._id || selectedUser?.id;
    if (!inputText.trim() || !recipientId || sending) return;

    const messageContent = inputText.trim();
    const contextToSend = activeContext;
    setInputText('');
    setSending(true);

    try {
      const sentMsg = await sendDirectMessage({
        recipientId,
        message: messageContent,
        studyId: contextToSend?.studyId || null,
        context: contextToSend || null
      });

      if (sentMsg) {
        setMessages((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const sentId = String(sentMsg._id || sentMsg.id || '');
          if (sentId && list.some((m) => String(m._id || m.id || '') === sentId)) {
            return list;
          }
          return [...list, sentMsg];
        });
      }
      // Detach context after initial message is sent so subsequent messages in this thread don't repeat the header
      if (activeContext) {
        setActiveContext(null);
      }
      // Update conversations list
      loadConversations();
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Message could not be delivered.');
    } finally {
      setSending(false);
    }
  };

  // Deduplicate and sort messages chronologically to guarantee correct date grouping and no duplicates
  const uniqueMessages = React.useMemo(() => {
    if (!Array.isArray(messages)) return [];
    const seen = new Set();
    const filtered = messages.filter((m) => {
      const id = String(m._id || m.id || '');
      if (id) {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      }
      return true;
    });
    return filtered.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
  }, [messages]);

  // Ensure selectedUser appears in the conversations list even if no prior messages exist in DB
  const displayConversations = React.useMemo(() => {
    const safeConversations = Array.isArray(conversations) ? conversations : [];
    // CRITICAL: Filter out any self-conversation
    const list = safeConversations.filter((c) => {
      const otherId = (c.otherUser?._id || c.partner?._id || c.otherUser?.id || c.partner?.id)?.toString();
      return otherId && otherId !== myId;
    });

    const targetId = (selectedUser?._id || selectedUser?.id)?.toString();
    if (
      targetId &&
      targetId !== myId &&
      !list.some((c) => (c.otherUser?._id || c.partner?._id || c.otherUser?.id || c.partner?.id)?.toString() === targetId)
    ) {
      list.unshift({
        otherUser: selectedUser,
        partner: selectedUser,
        lastMessage: {
          message: activeContext?.concerns
            ? `Flagged feedback: "${activeContext.concerns.substring(0, 50)}${activeContext.concerns.length > 50 ? '...' : ''}"`
            : 'Start of consultation...',
          createdAt: new Date()
        },
        unreadCount: 0,
        isPendingNew: true
      });
    }
    return list;
  }, [conversations, selectedUser, activeContext, myId]);

  // Filtered conversations
  const filteredConversations = displayConversations.filter((c) => {
    const other = c?.otherUser || c?.partner || {};
    const name = `${other.firstName || ''} ${other.lastName || ''}`.toLowerCase();
    const role = (other.role || '').toLowerCase();
    const q = (searchFilter || '').toLowerCase();
    return name.includes(q) || role.includes(q);
  });

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 120px)',
        minHeight: 520,
        bgcolor: isDark ? '#121212' : '#F8FAFC',
        borderRadius: '16px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: isDark ? alpha('#FFFFFF', 0.1) : alpha('#000000', 0.08),
        boxShadow: isDark
          ? '0 8px 32px rgba(0,0,0,0.5)'
          : '0 8px 32px rgba(15,23,42,0.06)'
      }}
    >
      {/* ─── LEFT: Conversations List ──────────────────────── */}
      <Box
        sx={{
          width: { xs: 120, sm: 320, md: 360 },
          borderRight: '1px solid',
          borderColor: isDark ? alpha('#FFFFFF', 0.08) : alpha('#000000', 0.08),
          display: 'flex',
          flexDirection: 'column',
          bgcolor: isDark ? '#18181B' : '#FFFFFF'
        }}
      >
        {/* Header / Search */}
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Typography variant="h6" fontWeight={700} sx={{ color: isDark ? '#F43F5E' : '#2563EB', display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChatIcon sx={{ fontSize: 22 }} />
              Messages
            </Typography>
            <Tooltip title="Refresh conversations">
              <IconButton size="small" onClick={loadConversations} sx={{ color: isDark ? '#A1A1AA' : '#64748B' }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <TextField
            size="small"
            fullWidth
            placeholder="Search conversations..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: isDark ? '#71717A' : '#94A3B8' }} />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: isDark ? '#27272A' : '#F1F5F9',
                borderRadius: '10px',
                fontSize: '0.85rem'
              }
            }}
          />
        </Box>

        <Divider />

        {/* Conversation Items */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loadingConversations ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress size={28} />
            </Box>
          ) : filteredConversations.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body2" color="textSecondary">
                {searchFilter ? 'No conversations matching search' : 'No conversations yet'}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filteredConversations.map((conv) => {
                const other = conv?.otherUser || conv?.partner;
                if (!other) return null;
                const otherId = other._id || other.id;
                const currentSelectedId = selectedUser?._id || selectedUser?.id;
                const isSelected = currentSelectedId && otherId && currentSelectedId === otherId;
                const unread = conv.unreadCount || 0;
                const name = `${other.firstName || ''} ${other.lastName || ''}`.trim() || other.email || 'User';

                return (
                  <ListItemButton
                    key={other._id}
                    selected={isSelected}
                    onClick={() => {
                      setSelectedUser(other);
                      setActiveContext(null); // Clear previous context unless explicitly set
                    }}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderLeft: isSelected ? '4px solid #2563EB' : '4px solid transparent',
                      bgcolor: isSelected
                        ? isDark
                          ? alpha('#2563EB', 0.2)
                          : alpha('#2563EB', 0.08)
                        : 'transparent',
                      '&:hover': {
                        bgcolor: isDark ? alpha('#FFFFFF', 0.04) : alpha('#000000', 0.03)
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        color="error"
                        badgeContent={unread}
                        invisible={unread === 0}
                        overlap="circular"
                      >
                        <Avatar
                          sx={{
                            bgcolor: isDark ? '#3B82F6' : '#2563EB',
                            fontWeight: 700,
                            width: 40,
                            height: 40,
                            fontSize: '0.9rem'
                          }}
                        >
                          {name.charAt(0).toUpperCase()}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography
                            variant="subtitle2"
                            fontWeight={unread > 0 ? 700 : 600}
                            noWrap
                            sx={{ color: isDark ? '#F1F5F9' : '#0F172A', maxWidth: 140 }}
                          >
                            {name}
                          </Typography>
                          {conv.lastMessage?.createdAt && (
                            <Typography variant="caption" sx={{ color: isDark ? '#71717A' : '#94A3B8', fontSize: '0.7rem' }}>
                              {formatConversationTimestamp(conv.lastMessage.createdAt)}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Box display="flex" alignItems="center" gap={0.75} mt={0.25}>
                          <Chip
                            label={other.role?.toUpperCase() || 'USER'}
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: '0.62rem',
                              fontWeight: 700,
                              bgcolor: isDark ? alpha('#3B82F6', 0.2) : alpha('#3B82F6', 0.1),
                              color: isDark ? '#93C5FD' : '#1D4ED8'
                            }}
                          />
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{
                              color: unread > 0 ? (isDark ? '#F87171' : '#DC2626') : (isDark ? '#A1A1AA' : '#64748B'),
                              fontWeight: unread > 0 ? 700 : 400,
                              flex: 1
                            }}
                          >
                            {conv.lastMessage?.message || ''}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </Box>

      {/* ─── RIGHT: Chat Pane ─────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: isDark ? '#121212' : '#F8FAFC' }}>
        {selectedUser ? (
          <>
            {/* Chat Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: isDark ? alpha('#FFFFFF', 0.08) : alpha('#000000', 0.08),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: isDark ? '#18181B' : '#FFFFFF'
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar
                  sx={{
                    bgcolor: isDark ? '#3B82F6' : '#2563EB',
                    width: 42,
                    height: 42,
                    fontWeight: 700
                  }}
                >
                  {(selectedUser.firstName || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: isDark ? '#F1F5F9' : '#0F172A', lineHeight: 1.2 }}>
                    {`${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || 'User'}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" mt={0.25}>
                    <Chip
                      label={selectedUser.role?.toUpperCase() || 'USER'}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                    />
                    {selectedUser.email && (
                      <Typography variant="caption" sx={{ color: isDark ? '#71717A' : '#94A3B8' }}>
                        {selectedUser.email}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                {userFlaggedReports.length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<FlagIcon sx={{ fontSize: 15 }} />}
                    onClick={(e) => setFlaggedMenuAnchor(e.currentTarget)}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      borderRadius: '8px',
                      py: 0.3,
                      px: 1.25,
                      borderColor: isDark ? alpha('#EF4444', 0.5) : alpha('#EF4444', 0.3),
                      bgcolor: isDark ? alpha('#EF4444', 0.12) : alpha('#EF4444', 0.06),
                      '&:hover': {
                        borderColor: '#EF4444',
                        bgcolor: isDark ? alpha('#EF4444', 0.2) : alpha('#EF4444', 0.12)
                      }
                    }}
                  >
                    {userFlaggedReports.length === 1 ? '1 Flagged Report' : `${userFlaggedReports.length} Flagged Reports`}
                  </Button>
                )}
                {onClose && (
                  <IconButton onClick={onClose} size="small">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>

            {/* Context Banner (e.g. Flagged Report Discussion) */}
            {activeContext && (
              <Box
                sx={{
                  px: 2.5,
                  py: 1,
                  bgcolor: isDark ? alpha('#EF4444', 0.15) : alpha('#EF4444', 0.08),
                  borderBottom: '1px solid',
                  borderColor: isDark ? alpha('#EF4444', 0.3) : alpha('#EF4444', 0.2),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.25
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
                  <FlagIcon sx={{ color: '#EF4444', fontSize: 18, flexShrink: 0 }} />
                  <Typography variant="caption" noWrap sx={{ color: isDark ? '#FECACA' : '#991B1B', fontWeight: 600 }}>
                    Active Context: Flagged {activeContext.modality || ''} Report{activeContext.patientName ? ` for ${formatPatientName(activeContext.patientName)}` : ''} — {activeContext.concerns ? `"${activeContext.concerns.substring(0, 70)}${activeContext.concerns.length > 70 ? '...' : ''}"` : 'Discussion'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                  {(activeContext.studyUID || activeContext.studyId) && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleOpenReportViewer(activeContext)}
                      sx={{
                        py: 0.2,
                        px: 1,
                        fontSize: '0.7rem',
                        textTransform: 'none',
                        borderRadius: '6px',
                        borderColor: isDark ? alpha('#EF4444', 0.5) : alpha('#EF4444', 0.4),
                        '&:hover': {
                          borderColor: '#EF4444',
                          bgcolor: alpha('#EF4444', 0.1)
                        }
                      }}
                    >
                      View Report ↗
                    </Button>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => setActiveContext(null)}
                    title="Dismiss Context"
                    sx={{
                      color: isDark ? '#FCA5A5' : '#DC2626',
                      p: 0.5,
                      '&:hover': { bgcolor: isDark ? alpha('#EF4444', 0.2) : alpha('#EF4444', 0.1) }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
              </Box>
            )}

            {/* Messages Stream */}
            <Box
              sx={{
                flex: 1,
                p: 3,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
              }}
            >
              {loadingMessages ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress size={32} />
                </Box>
              ) : (!Array.isArray(uniqueMessages) || uniqueMessages.length === 0) ? (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" color="text.secondary">
                  <ChatIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body1" fontWeight={600} sx={{ color: isDark ? '#F1F5F9' : '#0F172A' }}>
                    {selectedUser?.firstName ? `Chat with ${selectedUser.firstName} ${selectedUser.lastName || ''}`.trim() : 'Start the conversation'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" maxWidth={380} textAlign="center" mt={0.5}>
                    {activeContext?.concerns
                      ? `Hospital concerns were flagged: "${activeContext.concerns}". Send a message below to clarify details or discuss revisions.`
                      : 'Send a message below to start communicating.'}
                  </Typography>
                </Box>
              ) : (
                uniqueMessages.map((msg, index) => {
                  const senderId = (msg.sender?._id || msg.sender?.id || msg.sender)?.toString();
                  const isMine = Boolean(senderId && myId && senderId === myId);

                  const prevMsg = index > 0 ? uniqueMessages[index - 1] : null;
                  const showDateDivider = !prevMsg || getDateKey(msg.createdAt) !== getDateKey(prevMsg.createdAt);
                  const dateLabel = showDateDivider ? formatMessageDateDivider(msg.createdAt) : null;

                  return (
                    <React.Fragment key={msg._id || `msg-${index}`}>
                      {showDateDivider && dateLabel && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            my: 1.5,
                            userSelect: 'none'
                          }}
                        >
                          <Box
                            sx={{
                              px: 2,
                              py: 0.4,
                              borderRadius: '12px',
                              bgcolor: isDark ? alpha('#27272A', 0.95) : '#FFFFFF',
                              color: isDark ? '#A1A1AA' : '#64748B',
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              letterSpacing: '0.2px',
                              boxShadow: isDark
                                ? '0 1px 4px rgba(0,0,0,0.5)'
                                : '0 1px 4px rgba(0,0,0,0.08)',
                              border: '1px solid',
                              borderColor: isDark ? alpha('#FFFFFF', 0.08) : alpha('#000000', 0.07),
                              backdropFilter: 'blur(4px)'
                            }}
                          >
                            {dateLabel}
                          </Box>
                        </Box>
                      )}

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: isMine ? 'flex-end' : 'flex-start'
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            px: 2,
                            maxWidth: { xs: '85%', sm: '70%' },
                            borderRadius: isMine ? '14px 14px 2px 14px' : '14px 14px 14px 2px',
                            bgcolor: isMine
                              ? isDark
                                ? '#2563EB'
                                : '#2563EB'
                              : isDark
                              ? '#27272A'
                              : '#FFFFFF',
                            color: isMine ? '#FFFFFF' : (isDark ? '#F1F5F9' : '#0F172A'),
                            border: isMine ? 'none' : '1px solid',
                            borderColor: isDark ? alpha('#FFFFFF', 0.06) : alpha('#000000', 0.06),
                            boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
                          }}
                        >
                          {msg.context?.type === 'flagged_report' && (
                            <Box
                              sx={{
                                mb: 1,
                                p: 1,
                                borderRadius: '6px',
                                bgcolor: isMine ? 'rgba(0,0,0,0.2)' : (isDark ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.08)'),
                                borderLeft: '3px solid #EF4444',
                                fontSize: '0.75rem'
                              }}
                            >
                              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={0.25}>
                                <Typography variant="caption" fontWeight={700} display="block">
                                  🚩 Flagged Report Discussion
                                </Typography>
                                {(msg.context?.studyUID || msg.context?.studyId || msg.relatedStudy) && (
                                  <Box
                                    component="span"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenReportViewer(msg.context, msg.relatedStudy, msg.relatedReport);
                                    }}
                                    sx={{
                                      cursor: 'pointer',
                                      fontWeight: 700,
                                      textDecoration: 'underline',
                                      fontSize: '0.7rem',
                                      color: isMine ? '#93C5FD' : '#2563EB',
                                      '&:hover': { opacity: 0.8 },
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 0.3
                                    }}
                                  >
                                    View Report ↗
                                  </Box>
                                )}
                              </Box>
                              {msg.context?.concerns && (
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                  Concerns: {msg.context.concerns}
                                </Typography>
                              )}
                            </Box>
                          )}
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.45 }}>
                            {msg.message}
                          </Typography>
                        </Paper>

                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.4, px: 0.5 }}>
                          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: isDark ? '#71717A' : '#94A3B8' }}>
                            {msg.createdAt
                              ? new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                              : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                          {isMine && (
                            <DoneAllIcon
                              sx={{
                                fontSize: 14,
                                color: msg.read ? '#38BDF8' : (isDark ? '#71717A' : '#CBD5E1'),
                                transition: 'color 0.25s ease'
                              }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </React.Fragment>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Input Bar */}
            <Box
              component="form"
              onSubmit={handleSendMessage}
              sx={{
                p: 2,
                borderTop: '1px solid',
                borderColor: isDark ? alpha('#FFFFFF', 0.08) : alpha('#000000', 0.08),
                bgcolor: isDark ? '#18181B' : '#FFFFFF',
                display: 'flex',
                gap: 1.5,
                alignItems: 'center'
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder={`Message ${selectedUser.firstName || 'user'}... (Press Enter to send)`}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                disabled={sending}
                autoComplete="off"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: isDark ? '#27272A' : '#F8FAFC',
                    borderRadius: '12px'
                  }
                }}
              />
              <IconButton
                type="submit"
                disabled={sending || !inputText.trim()}
                sx={{
                  bgcolor: '#2563EB',
                  color: '#FFFFFF',
                  width: 42,
                  height: 42,
                  borderRadius: '12px',
                  '&:hover': { bgcolor: '#1D4ED8' },
                  '&.Mui-disabled': { bgcolor: isDark ? '#27272A' : '#E2E8F0', color: isDark ? '#52525B' : '#94A3B8' }
                }}
              >
                {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
              </IconButton>
            </Box>
          </>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" color="text.secondary">
            <ChatIcon sx={{ fontSize: 64, opacity: 0.25, mb: 2 }} />
            <Typography variant="h6" fontWeight={600} color="textPrimary">
              Direct Communication
            </Typography>
            <Typography variant="body2" color="textSecondary" maxWidth={360} textAlign="center" mt={0.5}>
              Select a conversation from the left or click "Message Reviewer" on any flagged report to chat directly with clinical staff and hospital administrators.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Flagged Reports Menu / Popover */}
      <Menu
        anchorEl={flaggedMenuAnchor}
        open={Boolean(flaggedMenuAnchor)}
        onClose={() => setFlaggedMenuAnchor(null)}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 460,
            p: 1,
            borderRadius: '14px',
            bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
            border: '1px solid',
            borderColor: isDark ? alpha('#FFFFFF', 0.1) : alpha('#000000', 0.08),
            boxShadow: isDark ? '0 16px 40px rgba(0,0,0,0.8)' : '0 8px 30px rgba(0,0,0,0.12)'
          }
        }}
      >
        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: isDark ? alpha('#FFFFFF', 0.08) : alpha('#000000', 0.06), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: isDark ? '#F1F5F9' : '#0F172A' }}>
            🚩 Flagged Reports ({userFlaggedReports.length})
          </Typography>
          <Typography variant="caption" sx={{ color: isDark ? '#A1A1AA' : '#64748B' }}>
            Select to view or discuss
          </Typography>
        </Box>
        <List disablePadding sx={{ mt: 1, maxHeight: 380, overflowY: 'auto' }}>
          {userFlaggedReports.map((report) => {
            const pName = formatPatientName(report.patientName || report.study?.patientName) || 'Patient';
            const mod = report.modality || report.study?.modality || '—';
            const concernsText = report.facilityReview?.concerns || 'Hospital requested review/revision';
            const fac = report.facilityName || report.facilityReview?.facilityName || report.study?.facilityName || 'Hospital';

            return (
              <Box
                key={report._id}
                sx={{
                  p: 1.5,
                  mb: 1.25,
                  borderRadius: '10px',
                  bgcolor: isDark ? alpha('#EF4444', 0.08) : '#FEF2F2',
                  border: '1px solid',
                  borderColor: isDark ? alpha('#EF4444', 0.25) : alpha('#EF4444', 0.18)
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" fontWeight={700} sx={{ color: isDark ? '#F1F5F9' : '#0F172A' }}>
                    {pName}
                  </Typography>
                  <Chip
                    label={mod}
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 800 }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: isDark ? '#FCA5A5' : '#991B1B', display: 'block', mb: 1.25, lineHeight: 1.35 }}>
                  <strong>{fac}:</strong> "{concernsText.substring(0, 90)}{concernsText.length > 90 ? '...' : ''}"
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      handleOpenReportViewer(
                        {
                          studyUID: report.study?.studyInstanceUID || report.studyInstanceUID,
                          studyId: report.study?._id || report.study,
                          reportId: report._id
                        },
                        report.study,
                        report
                      );
                      setFlaggedMenuAnchor(null);
                    }}
                    sx={{
                      fontSize: '0.72rem',
                      py: 0.25,
                      px: 1.25,
                      textTransform: 'none',
                      borderRadius: '6px',
                      color: isDark ? '#93C5FD' : '#2563EB',
                      borderColor: isDark ? alpha('#93C5FD', 0.4) : alpha('#2563EB', 0.3)
                    }}
                  >
                    View Report ↗
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => handleSelectReportContext(report)}
                    sx={{
                      fontSize: '0.72rem',
                      py: 0.25,
                      px: 1.25,
                      textTransform: 'none',
                      borderRadius: '6px'
                    }}
                  >
                    Discuss in Chat
                  </Button>
                </Stack>
              </Box>
            );
          })}
        </List>
      </Menu>

      {/* Report Viewer Dialog for Flagged / Context Reports */}
      <Dialog
        open={reportViewerState.open}
        onClose={() => setReportViewerState({ open: false, studyId: null, study: null, reportType: 'dycovue-report' })}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            width: { xs: '95vw', sm: '92vw', md: '860px', lg: '920px' },
            maxWidth: '940px !important',
            maxHeight: '88vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}` }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.98rem', color: isDark ? '#F1F5F9' : '#0F172A' }}>
              Report Details & Hospital Review
            </Typography>
            <IconButton
              size="small"
              onClick={() => setReportViewerState({ open: false, studyId: null, study: null, reportType: 'dycovue-report' })}
              sx={{ color: isDark ? '#94A3B8' : '#64748B' }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 }, overflowY: 'auto', flexGrow: 1 }}>
          {reportViewerState.studyId && (
            <ReportViewer
              studyId={reportViewerState.studyId}
              study={reportViewerState.study}
              reportType={reportViewerState.reportType || 'dycovue-report'}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
